import { FC, useState, Fragment, useEffect } from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { ChevronRight, ExpandLess, ExpandMore, Folder, FolderOpen, BarChart } from '@mui/icons-material';
import { EntityDTO, Item as ItemDTO } from 'TaxAnalyticsHelpers/TaxAnalyticsClient';
import { useRecoilValue } from 'recoil';
import { selectedEntityState } from 'TaxAnalyticsState/userState';
import { Collapse, ListItemIcon, IconButton, ListItemSecondaryAction, ListItemText, Divider, ListItem, List, ListItemProps } from '@mui/material';
import { truncateString } from 'TaxAnalyticsHelpers';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        subRoot: {
            width: '100%',
            backgroundColor: theme.palette.background.paper,
            padding: 0,
            margin: 0
        },
        root: {
            width: '100%',
            backgroundColor: theme.palette.background.paper,
            padding: 0,
            margin: 0,
            overflow: 'auto',
            height: '100%',
            maxHeight: '100%'
        },
        inline: {
            display: 'inline',
        },
        nested: {
            paddingLeft: theme.spacing(4)
        },
        wrap: {
            overflowWrap: 'anywhere'
        }
    }),
);

export interface Item extends ItemDTO {
    action?: (item: Item) => void;
    icon?: any;
}

interface DeloitteListProps {
    items: Item[]
    selectedItem?: string;
    level?: number;
    defaultAction?: (item: Item) => void;
}
interface DeloitteEntityListProps {
    entities: EntityDTO[],
    selectionCallback: (entity: EntityDTO) => void,
    detailed?: boolean
}
export const DeloitteList: FC<DeloitteListProps> = (props) => {
    const classes = useStyles();
    const { items, selectedItem, defaultAction, level = 0 } = props;
    return (
        <Fragment>
            <List className={level !== 0 ? classes.subRoot : classes.root} >
                {items.map((item, index) => {
                    return (<DeloitteExpandableItem key={index} item={item} selectedItem={selectedItem} level={level} defaultAction={defaultAction} />)
                }
                )}
            </List>
        </Fragment >
    );
}

interface IDeloitteExpandableItem extends ListItemProps {
    item: Item;
    selectedItem?: string;
    level?: number;
    defaultAction?: (item: Item) => void;
    wrap?: boolean;
}

const shouldOpen = (item: Item, selectedItemId: string | undefined): boolean => {
    let result = false;
    if (item.id === selectedItemId)
        return true;
    if (item.children) {
        for (var child of item.children) {
            if (child.id === selectedItemId) {
                return true;
            }
            if (child.children) {
                if (shouldOpen(child, selectedItemId))
                    return true;
            }
        }
    }
    return result;
}

export const DeloitteExpandableItem: FC<IDeloitteExpandableItem> = (props) => {
    const theme = useTheme();
    const classes = useStyles();
    const { item, selectedItem, defaultAction, wrap, level = 0 } = props;
    const [open, setOpen] = useState(false);
    const hasChildren = item && item.children && item.children.length > 0;
    const Icon = item.icon;
    const actionToExecute = () => {
        if (hasChildren) {
            setOpen(!open);
            return;
        }
        if (item.action) {
            item.action(item);
            return;
        }
        if (defaultAction) {
            defaultAction(item);
        }
    }
    useEffect(() => {
        setOpen(shouldOpen(item, selectedItem));
    }, [selectedItem]);
    return (
        <Fragment>
            <ListItem button onClick={() => actionToExecute()} selected={selectedItem == item.id} style={{ paddingLeft: theme.spacing(level + 2) }}>
                {hasChildren &&
                    <ListItemIcon>
                        {open ? <FolderOpen /> : <Folder />}
                    </ListItemIcon>
                }
                {!hasChildren &&
                    <ListItemIcon>
                        {item.icon ? <Icon /> : <BarChart />}
                    </ListItemIcon>
                }
                <ListItemText
                    primary={truncateString(item.primary, 80)}
                    secondary={truncateString(item.secondary, 80)}
                    className={classes.wrap}
                />
                {hasChildren &&
                    <IconButton edge="end" aria-label="expand" size="large">
                        {open ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                }
                {!hasChildren &&
                    <IconButton edge="end" aria-label="comments" size="large">
                        <ChevronRight />
                    </IconButton>
                }
            </ListItem>
            {hasChildren &&
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Divider component="li" />
                    <DeloitteList items={item.children!} selectedItem={selectedItem} level={level + 1} defaultAction={defaultAction} />
                </Collapse>
            }
            <Divider component="li" />
        </Fragment>
    );
}

export const DeloitteEntityList: FC<DeloitteEntityListProps> = (props) => {
    const classes = useStyles();
    const { entities, selectionCallback, detailed } = props;
    const selectedEntity = useRecoilValue(selectedEntityState)

    return (
        <List className={classes.root}>
            {entities && entities.map((entity, index) =>
                <ListItem button alignItems="flex-start" key={index} onClick={() => selectionCallback(entity)} selected={selectedEntity?.id === entity.id}>
                    <ListItemText
                        primary={truncateString(entity.name, 80)}
                        secondary={detailed ? `Créateur : ${entity?.creatorName}` : ""}
                        className={classes.wrap}
                    />
                    <ListItemSecondaryAction>
                        <IconButton
                            edge="end"
                            aria-label="comments"
                            onClick={() => selectionCallback(entity)}
                            size="large">
                            <ChevronRight />
                        </IconButton>
                    </ListItemSecondaryAction>
                </ListItem>
            )}
            <Divider component="li" />
        </List>
    );
}
